"use client";

import { useState } from "react";
import {
  Download,
  Save,
  FileText,
  Award,
  Heart,
  BarChart3,
  RefreshCw,
  Users,
  Trophy,
  Crown,
  Brain,
  Shield,
  Star,
  UserCheck,
} from "lucide-react";
import "./team-team.css";
import BackButton from "../../shared/backButton";
import Modal from "../../layout/Modal";

const CombinedTalentReport = ({ comparisonData }) => {
  console.log(comparisonData, "comparisonData from <TeamTeamReport />");
  const [activeSection, setActiveSection] = useState("overview");
  const [activeSubTab, setActiveSubTab] = useState("analysis");
  const [isSaving, setIsSaving] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showFilenameModal, setShowFilenameModal] = useState(false);
  const [filename, setFilename] = useState("");

  // Combined data from all three reports
  const combinedData = {
    title: "Comprehensive Team Analysis Report",
    teams: [
      {
        name: "Product Development",
        size: 12,
        department: "Engineering",
        culture: "Innovation-focused, collaborative, detail-oriented",
        description:
          "Responsible for product development and technical implementation",
        teamLead: "Sarah Johnson",
        avgPerformance: 85,
        openPositions: 2,
        // Skill Gap Data
        skillGapPercentage: 30,
        missingSkills: ["Adaptability", "Time Management", "Communication"],
        gapSkills: ["Adaptability", "Time Management", "Communication"],
        // Personality Data
        highestSkill: "Profession",
        highestScore: 6,
        // Leadership Data
        leadershipSkillCount: 26,
        promotionReady: { count: 5, percentage: 12.5 },
        leadershipReady: { count: 7, percentage: 17.5 },
        nearbyReady: { count: 14, percentage: 35.0 },
        nominatedForLeadership: 5,
      },
      {
        name: "Product Management",
        size: 8,
        department: "Product",
        culture: "Strategic, goal-oriented, high-achieving",
        description: "Manages product strategy and roadmap planning",
        teamLead: "Michael Chen",
        avgPerformance: 92,
        openPositions: 1,
        // Skill Gap Data
        skillGapPercentage: 20,
        missingSkills: ["Leadership", "Problem Solving"],
        gapSkills: ["Leadership", "Problem Solving"],
        // Personality Data
        highestSkill: "Profession",
        highestScore: 9,
        // Leadership Data
        leadershipSkillCount: 65,
        promotionReady: { count: 30, percentage: 15.0 },
        leadershipReady: { count: 20, percentage: 10.0 },
        nearbyReady: { count: 15, percentage: 7.5 },
        nominatedForLeadership: 30,
      },
    ],
    // Analysis summaries from each report
    skillGapAnalysis:
      "Team Product Development is missing 3 out of 10 benchmarked soft skills, representing a 30% gap. The specific missing skills for this team are Adaptability, Time Management, and Communication. In contrast, Team Product Management is missing 2 out of 10 soft skills, equating to a 20% gap, with the missing skills being Leadership and Problem Solving. Thus, Product Development has a slightly higher proportion of soft skill gaps and is missing one more skill compared to Product Management.",
    personalityAnalysis:
      "Product Development demonstrates moderate spirit (avg 5) with significant diversity, suggesting a mix of collaborative and independent mindsets, while Product Management exhibits a uniformly high spirit (avg 8), indicating a consistently strong team orientation. On the reward axis, Product Development's lower average (3) and broad range reflect a team with varied motivations and less emphasis on external recognition, whereas Product Management's high and uniform reward scores (avg 8) point to a collective drive for achievement and acknowledgment.",
    leadershipAnalysis:
      "Team A has 5 promotion-ready members (12.5%), 7 leadership-ready members (17.5%), and 14 nearby-ready members (35.0%) out of 40, while Team B has 30 promotion-ready (15.0%), 20 leadership-ready (10.0%), and 15 nearby-ready (7.5%) out of 200.",
    // Executive summaries
    skillGapSummary:
      "For Product Development, the absence of Adaptability (impacting Delivery) poses a risk of resistance to change, slow pivots, and delays in handling scope changes, which could hinder the team's ability to respond efficiently to evolving project requirements. Missing Time Management (Delivery) increases the likelihood of missed deadlines, rushed work, and unpredictable delivery, directly threatening the team's ability to meet commitments. The lack of Communication (impacting both Collaboration and Delivery) is particularly critical, as it can lead to misunderstandings, misalignment, and inefficient collaboration or execution, undermining both team cohesion and project outcomes.",
    personalitySummary:
      "These contrasts suggest that Product Management will likely operate with greater cohesion, clarity, and motivation, facilitating swift decision-making and alignment, while Product Development's diversity may foster creativity and adaptability but could require more effort to unify perspectives and sustain momentum in collaborative projects.",
    leadershipSummary:
      "In absolute terms, Team B has a significantly larger pool in each category, but proportionally, Team A has a higher percentage of leadership-ready and nearby-ready talent, while Team B leads slightly in promotion-ready proportion. Team A demonstrates greater leadership maturity through its combined 30.0% promotion and leadership-ready members versus Team B's 25.0%. However, Team A's near-ready pool is notably larger by percentage, indicating broader growth potential, whereas Team B's near-ready group is smaller relative to its size. These dynamics suggest that while Team B has greater immediate bench strength due to its scale, Team A's higher readiness ratios and near-ready pipeline position it well for future succession, though both teams may benefit from targeted development to balance immediate and long-term leadership needs.",
    // Soft Skills Data
    softSkillGaps: [
      {
        skill: "Adaptability",
        team1Score: 65,
        team2Score: 85,
        team1Benchmark: 80,
        team2Benchmark: 75,
      },
      {
        skill: "Time Management",
        team1Score: 70,
        team2Score: 88,
        team1Benchmark: 85,
        team2Benchmark: 82,
      },
      {
        skill: "Communication",
        team1Score: 72,
        team2Score: 90,
        team1Benchmark: 85,
        team2Benchmark: 87,
      },
      {
        skill: "Leadership",
        team1Score: 88,
        team2Score: 75,
        team1Benchmark: 85,
        team2Benchmark: 80,
      },
      {
        skill: "Problem Solving",
        team1Score: 85,
        team2Score: 78,
        team1Benchmark: 82,
        team2Benchmark: 85,
      },
      {
        skill: "Teamwork",
        team1Score: 92,
        team2Score: 89,
        team1Benchmark: 88,
        team2Benchmark: 85,
      },
      {
        skill: "Critical Thinking",
        team1Score: 87,
        team2Score: 91,
        team1Benchmark: 85,
        team2Benchmark: 88,
      },
      {
        skill: "Creativity",
        team1Score: 79,
        team2Score: 84,
        team1Benchmark: 75,
        team2Benchmark: 80,
      },
      {
        skill: "Emotional Intelligence",
        team1Score: 83,
        team2Score: 86,
        team1Benchmark: 80,
        team2Benchmark: 82,
      },
      {
        skill: "Conflict Resolution",
        team1Score: 76,
        team2Score: 82,
        team1Benchmark: 78,
        team2Benchmark: 85,
      },
    ],
    // Personality Metrics Data
    personalityMetrics: [
      {
        skill: "Spirit",
        team1Score: 5,
        team2Score: 8,
        benchmark: 7,
        team1Range: "2-7",
        team2Range: "7-10",
      },
      {
        skill: "Reward",
        team1Score: 3,
        team2Score: 8,
        benchmark: 6,
        team1Range: "2-7",
        team2Range: "7-10",
      },
      {
        skill: "Profession",
        team1Score: 6,
        team2Score: 9,
        benchmark: 7,
        team1Range: "1-7",
        team2Range: "8-10",
      },
      {
        skill: "Purpose",
        team1Score: 3,
        team2Score: 7,
        benchmark: 6,
        team1Range: "1-6",
        team2Range: "4-9",
      },
    ],
    // Leadership Metrics Data
    leadershipMetrics: [
      {
        skill: "Promotion Ready",
        team1Count: 5,
        team1Percentage: 12.5,
        team2Count: 30,
        team2Percentage: 15.0,
        benchmark: 14,
      },
      {
        skill: "Leadership Ready",
        team1Count: 7,
        team1Percentage: 17.5,
        team2Count: 20,
        team2Percentage: 10.0,
        benchmark: 12,
      },
      {
        skill: "Nearby Ready",
        team1Count: 14,
        team1Percentage: 35.0,
        team2Count: 15,
        team2Percentage: 7.5,
        benchmark: 20,
      },
    ],
  };

  const sections = [
    { id: "overview", label: "Overview", icon: BarChart3 },
    { id: "teamDetails", label: "Team Details", icon: Users },
    { id: "softSkillGaps", label: "Soft Skills Gaps", icon: Heart },
    { id: "personalityMetrics", label: "Personality Metrics", icon: Brain },
    { id: "leadershipMetrics", label: "Leadership Metrics", icon: Shield },
    { id: "summary", label: "Summary", icon: FileText },
  ];

  const isGapSkill = (skill, teamIndex) => {
    return combinedData.teams[teamIndex].gapSkills.includes(skill);
  };

  const renderSubTabs = (sectionId) => {
    if (
      !["softSkillGaps", "personalityMetrics", "leadershipMetrics"].includes(
        sectionId
      )
    ) {
      return null;
    }

    return (
      <div className="combined-sub-tabs">
        <button
          className={`combined-sub-tab ${
            activeSubTab === "analysis" ? "active" : ""
          }`}
          onClick={() => setActiveSubTab("analysis")}
        >
          Analysis
        </button>
        <button
          className={`combined-sub-tab ${
            activeSubTab === "comparison" ? "active" : ""
          }`}
          onClick={() => setActiveSubTab("comparison")}
        >
          {sectionId === "softSkillGaps"
            ? "Soft Skill Gaps Comparison"
            : sectionId === "personalityMetrics"
            ? "Personality Metrics Comparison"
            : "Leadership Skill Comparison"}
        </button>
      </div>
    );
  };

  const renderSection = () => {
    switch (activeSection) {
      case "overview":
        return (
          <div className="combined-section-content">
            <div className="combined-overview-grid">
              <p>{comparisonData?.detailedReport?.teamADetails?.teamName}</p>
              <p>{comparisonData?.detailedReport?.teamBDetails?.teamName}</p>
            </div>

            <div className="combined-overview-grid">
              {/* Entities Compared */}
              {/*<div className="combined-overview-card combined-primary">
                <div className="combined-overview-icon">
                  <Users size={24} />
                </div>
                <div className="combined-overview-content">
                  <h3>Entities Compared</h3>
                  <div className="combined-score-display">2 Teams</div>
                  <p>Teams analyzed</p>
                </div>
              </div>*/}

              {/* High Performers Count */}
              <div className="combined-overview-card combined-secondary">
                <div className="combined-overview-icon">
                  <Trophy size={24} />
                </div>
                <div className="combined-overview-content">
                  <h3> High Performers Count</h3>
                  <div className="combined-score-display">
                    {comparisonData?.detailedReport?.teamAHighperformersCount}{" "}
                    Members
                  </div>
                  <p>Top performing members</p>
                </div>
              </div>

              {/* High Performers Count */}
              <div className="combined-overview-card combined-secondary">
                <div className="combined-overview-icon">
                  <Trophy size={24} />
                </div>
                <div className="combined-overview-content">
                  <h3> High Performers Count</h3>
                  <div className="combined-score-display">
                    {comparisonData?.detailedReport?.teamBHighperformersCount}{" "}
                    Members
                  </div>
                  <p>Top performing members</p>
                </div>
              </div>

              {/* Leadership Ready Count */}
              {/*<div className="combined-overview-card combined-tertiary">
                <div className="combined-overview-icon">
                  <Crown size={24} />
                </div>
                <div className="combined-overview-content">
                  <h3> Leadership Ready Count</h3>
                  <div className="combined-score-display">3 Members</div>
                  <p>Ready for leadership roles</p>
                </div>
              </div>*/}

              {/* Leadership Ready Count */}
              {/*<div className="combined-overview-card combined-tertiary">
                <div className="combined-overview-icon">
                  <Crown size={24} />
                </div>
                <div className="combined-overview-content">
                  <h3> Leadership Ready Count</h3>
                  <div className="combined-score-display">4 Members</div>
                  <p>Ready for leadership roles</p>
                </div>
              </div>*/}

              {/*  Skill with highest avg */}
              <div className="combined-overview-card combined-accent">
                <div className="combined-overview-icon">
                  <Star size={24} />
                </div>
                <div className="combined-overview-content">
                  <h3> Skill with highest avg</h3>
                  <div className="combined-score-display">
                    {
                      comparisonData?.detailedReport?.teamAHighestAvgAptitude
                        ?.aptitude
                    }
                  </div>
                  <p>
                    Score:{" "}
                    {
                      comparisonData?.detailedReport?.teamAHighestAvgAptitude
                        ?.avgScore
                    }
                    /10
                  </p>
                </div>
              </div>

              {/*  Skill with highest avg */}
              <div className="combined-overview-card combined-success">
                <div className="combined-overview-icon">
                  <Star size={24} />
                </div>
                <div className="combined-overview-content">
                  <h3> Skill with highest avg</h3>
                  <div className="combined-score-display">
                    {
                      comparisonData?.detailedReport?.teamBHighestAvgAptitude
                        ?.aptitude
                    }
                  </div>
                  <p>
                    Score:{" "}
                    {
                      comparisonData?.detailedReport?.teamBHighestAvgAptitude
                        ?.avgScore
                    }
                    /10
                  </p>
                </div>
              </div>

              {/* Team A Leadership Ready */}
              <div className="combined-overview-card combined-info">
                <div className="combined-overview-icon">
                  <Award size={24} />
                </div>
                <div className="combined-overview-content">
                  <h3>Team A Leadership Ready</h3>
                  <div className="combined-score-display">
                    {comparisonData?.detailedReport?.teamALeadershipReadyCount}{" "}
                    Members
                  </div>
                  {/* <p>
                    {combinedData.teams[0].leadershipReady.percentage}% of team
                  </p> */}
                </div>
              </div>

              {/* Team B Leadership Ready */}
              <div className="combined-overview-card combined-warning">
                <div className="combined-overview-icon">
                  <Award size={24} />
                </div>
                <div className="combined-overview-content">
                  <h3>Team B Leadership Ready</h3>
                  <div className="combined-score-display">
                    {comparisonData?.detailedReport?.teamBLeadershipReadyCount}{" "}
                    Members
                  </div>
                  {/* <p>
                    {combinedData.teams[1].leadershipReady.percentage}% of team
                  </p> */}
                </div>
              </div>

              {/* Team A Nominations */}
              <div className="combined-overview-card combined-danger">
                <div className="combined-overview-icon">
                  <Heart size={24} />
                </div>
                <div className="combined-overview-content">
                  <h3>Team A Nominations</h3>
                  <div className="combined-score-display">
                    {comparisonData?.detailedReport?.teamANominationsCount}{" "}
                    Members
                  </div>
                  <p>Nominated for leadership</p>
                </div>
              </div>

              {/* Team B Nominations */}
              <div className="combined-overview-card combined-dark">
                <div className="combined-overview-icon">
                  <Heart size={24} />
                </div>
                <div className="combined-overview-content">
                  <h3>Team B Nominations</h3>
                  <div className="combined-score-display">
                    {comparisonData?.detailedReport?.teamBNominationsCount}{" "}
                    Members
                  </div>
                  <p>Nominated for leadership</p>
                </div>
              </div>
            </div>
          </div>
        );

      case "teamDetails":
        return (
          <div className="combined-section-content">
            <h3 style={{ marginBottom: "10px" }}>Team Details</h3>
            <p style={{ marginBottom: "20px" }}>
              Comprehensive team profiles including size, department,
              leadership, performance metrics, and descriptions.
            </p>
            <div className="combined-team-details-grid">
              {/* {combinedData.teams.map((team, index) => ( */}
              <div className="combined-team-card">
                <div className="combined-team-header">
                  <h4>
                    {comparisonData?.detailedReport?.teamADetails?.teamName}
                  </h4>
                </div>
                <div className="combined-team-info">
                  <div className="combined-info-item">
                    <span className="combined-info-label">Team Size:</span>
                    <span className="combined-info-value">
                      {
                        comparisonData?.detailedReport?.teamADetails?.members
                          ?.length
                      }{" "}
                      members
                    </span>
                  </div>
                  <div className="combined-info-item">
                    <span className="combined-info-label">Department:</span>
                    <span className="combined-info-value">
                      {comparisonData?.detailedReport?.teamADetails?.department}
                    </span>
                  </div>
                  <div className="combined-info-item">
                    <span className="combined-info-label">Team Lead:</span>
                    <span className="combined-info-value">
                      {
                        comparisonData?.detailedReport?.teamADetails?.teamLead
                          .name
                      }
                    </span>
                  </div>
                  <div className="combined-info-item">
                    <span className="combined-info-label">
                      Avg Performance:
                    </span>
                    <span className="combined-info-value">
                      {comparisonData?.detailedReport?.teamADetails?.teamPerformance?.toFixed(
                        2
                      )}
                      %
                    </span>
                  </div>
                  <div className="combined-info-item">
                    <span className="combined-info-label">Open Positions:</span>
                    <span className="combined-info-value">
                      {
                        comparisonData?.detailedReport?.teamADetails
                          ?.openPositions
                      }
                    </span>
                  </div>
                  <div className="combined-info-item">
                    <span className="combined-info-label">Team Culture:</span>
                    <span className="combined-info-value">
                      {
                        comparisonData?.detailedReport?.teamADetails
                          ?.teamCulture
                      }
                    </span>
                  </div>
                  <div className="combined-info-item">
                    <span className="combined-info-label">
                      Leadership Skills:
                    </span>
                    <span className="combined-info-value">
                      {
                        comparisonData?.detailedReport?.teamADetails
                          ?.leadershipReady
                      }{" "}
                      people
                    </span>
                  </div>
                </div>
                <div className="combined-team-description">
                  <span className="combined-info-label">Team Description:</span>
                  <p>
                    {comparisonData?.detailedReport?.teamADetails?.description}
                  </p>
                </div>
              </div>
              {/* ))} */}

              <div className="combined-team-card">
                <div className="combined-team-header">
                  <h4>
                    {comparisonData?.detailedReport?.teamBDetails?.teamName}
                  </h4>
                </div>
                <div className="combined-team-info">
                  <div className="combined-info-item">
                    <span className="combined-info-label">Team Size:</span>
                    <span className="combined-info-value">
                      {
                        comparisonData?.detailedReport?.teamBDetails?.members
                          ?.length
                      }{" "}
                      members
                    </span>
                  </div>
                  <div className="combined-info-item">
                    <span className="combined-info-label">Department:</span>
                    <span className="combined-info-value">
                      {comparisonData?.detailedReport?.teamBDetails?.department}
                    </span>
                  </div>
                  <div className="combined-info-item">
                    <span className="combined-info-label">Team Lead:</span>
                    <span className="combined-info-value">
                      {
                        comparisonData?.detailedReport?.teamBDetails?.teamLead
                          .name
                      }
                    </span>
                  </div>
                  <div className="combined-info-item">
                    <span className="combined-info-label">
                      Avg Performance:
                    </span>
                    <span className="combined-info-value">
                      {comparisonData?.detailedReport?.teamBDetails?.teamPerformance?.toFixed(
                        2
                      )}
                      %
                    </span>
                  </div>
                  <div className="combined-info-item">
                    <span className="combined-info-label">Open Positions:</span>
                    <span className="combined-info-value">
                      {
                        comparisonData?.detailedReport?.teamBDetails
                          ?.openPositions
                      }
                    </span>
                  </div>
                  <div className="combined-info-item">
                    <span className="combined-info-label">Team Culture:</span>
                    <span className="combined-info-value">
                      {
                        comparisonData?.detailedReport?.teamBDetails
                          ?.teamCulture
                      }
                    </span>
                  </div>
                  <div className="combined-info-item">
                    <span className="combined-info-label">
                      Leadership Skills:
                    </span>
                    <span className="combined-info-value">
                      {
                        comparisonData?.detailedReport?.teamBDetails
                          ?.leadershipReady
                      }{" "}
                      people
                    </span>
                  </div>
                </div>
                <div className="combined-team-description">
                  <span className="combined-info-label">Team Description:</span>
                  <p>
                    {comparisonData?.detailedReport?.teamBDetails?.description}
                  </p>
                </div>
              </div>
            </div>
          </div>
        );

      case "softSkillGaps":
        return (
          <div className="combined-section-content">
            {renderSubTabs("softSkillGaps")}
            {activeSubTab === "analysis" ? (
              <div className="combined-analysis-container">
                <h3>Soft Skills Analysis</h3>
                <div className="combined-analysis-content">
                  <p>
                    {comparisonData?.detailedReport?.softSkillGaps?.summary}
                  </p>
                </div>
              </div>
            ) : (
              <div>
                <h3 style={{ marginBottom: "10px" }}>
                  Soft Skill Gaps Comparison
                </h3>
                <p style={{ marginBottom: "20px" }}>
                  Side-by-side soft-skill scores for both teams against their
                  respective benchmarks. Bars show attainment; the vertical
                  lines indicate each team's benchmark target. Gap skills are
                  highlighted in red.
                </p>
                <div className="combined-soft-skills-grid">
                  {comparisonData.detailedReport.softSkillGaps.gaps.map(
                    (skillData, index) => (
                      <div key={index} className="combined-soft-skill-card">
                        <div className="combined-soft-skill-header">
                          <h4>{skillData.skill}</h4>
                        </div>

                        {skillData.teams.map((team, index) => {
                          return (
                            <div className="combined-comparison-bars">
                              <div className="combined-team-comparison">
                                <div
                                  className={`combined-team-bar ${
                                    isGapSkill(skillData.skill, 0)
                                      ? "combined-has-gap"
                                      : ""
                                  }`}
                                >
                                  <span
                                    className={`combined-team-label ${
                                      isGapSkill(skillData.skill, 0)
                                        ? "combined-has-gap"
                                        : ""
                                    }`}
                                  >
                                    {team.name}
                                    {team.isGap && (
                                      <span className="combined-gap-indicator">
                                        Gap
                                      </span>
                                    )}
                                  </span>
                                  <div className="combined-progress-container">
                                    <div className="combined-progress-bar">
                                      <div
                                        className={
                                          "combined-progress-fill combined-team1"
                                        }
                                        style={{
                                          width: `${team.avgValue * 10}%`,
                                        }}
                                      ></div>
                                      <div
                                        className="combined-benchmark-line combined-team1-benchmark-line"
                                        style={{
                                          left: `${team.benchmarkScore * 10}Z`,
                                        }}
                                      ></div>
                                    </div>
                                    <span className="combined-score">
                                      {team.avgValue}
                                    </span>
                                    <span className="combined-team-benchmark combined-team1-benchmark">
                                      B: {team.benchmarkScore}
                                    </span>
                                  </div>
                                </div>
                                {/* <div
                              className={`combined-team-bar ${
                                isGapSkill(skillData.skill, 1)
                                  ? "combined-has-gap"
                                  : ""
                              }`}
                            >
                              <span
                                className={`combined-team-label ${
                                  isGapSkill(skillData.skill, 1)
                                    ? "combined-has-gap"
                                    : ""
                                }`}
                              >
                                {combinedData.teams[1].name}
                                {isGapSkill(skillData.skill, 1) && (
                                  <span className="combined-gap-indicator">
                                    Gap
                                  </span>
                                )}
                              </span>
                              <div className="combined-progress-container">
                                <div className="combined-progress-bar">
                                  <div
                                    className={
                                      "combined-progress-fill combined-team2"
                                    }
                                    style={{
                                      width: `${skillData.team2Score}%`,
                                    }}
                                  ></div>
                                  <div
                                    className="combined-benchmark-line combined-team2-benchmark-line"
                                    style={{
                                      left: `${skillData.team2Benchmark}%`,
                                    }}
                                  ></div>
                                </div>
                                <span className="combined-score">
                                  {skillData.team2Score}%
                                </span>
                                <span className="combined-team-benchmark combined-team2-benchmark">
                                  B: {skillData.team2Benchmark}%
                                </span>
                              </div>
                            </div> */}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )
                  )}
                </div>
              </div>
            )}
          </div>
        );

      case "personalityMetrics":
        return (
          <div className="combined-section-content">
            {renderSubTabs("personalityMetrics")}
            {activeSubTab === "analysis" ? (
              <div className="combined-analysis-container">
                <h3>Personality Analysis</h3>
                <div className="combined-analysis-content">
                  <p>
                    {
                      comparisonData?.detailedReport?.personalityMetrics
                        ?.summary
                    }
                  </p>
                </div>
              </div>
            ) : (
              <div>
                <h3 style={{ marginBottom: "10px" }}>
                  Personality Metrics Comparison
                </h3>
                <p style={{ marginBottom: "20px" }}>
                  Trait scores (0–10) for both teams vs. benchmark, with ranges
                  showing variability; bars visualize distance to the benchmark.
                </p>
                <div className="combined-personality-metrics-grid">
                  {comparisonData?.detailedReport?.personalityMetrics?.metrics.map(
                    (metricData, index) => (
                      <div
                        key={index}
                        className="combined-personality-metric-card"
                      >
                        <div className="combined-personality-metric-header">
                          <h4>{metricData.aptitude}</h4>
                          {/*<span className="combined-personality-benchmark">Benchmark: {metricData.benchmark}/10</span>*/}
                        </div>

                        {/* {metricData.teams(team,index)=>{}} */}
                        {metricData.teams.map((team, index) => {
                          return (
                            <div className="combined-personality-comparison-bars">
                              <div className="combined-personality-team-comparison">
                                <div className="combined-personality-team-bar">
                                  <span className="combined-personality-team-label">
                                    {team.name}
                                  </span>
                                  <div className="combined-personality-progress-container">
                                    <div className="combined-personality-progress-bar">
                                      <div
                                        className="combined-personality-progress-fill combined-personality-team1"
                                        style={{
                                          width: `${
                                            (team.avgScore / 10) * 100
                                          }%`,
                                        }}
                                      ></div>
                                      {/*<div
                                  className="combined-personality-benchmark-line"
                                  style={{ left: `${(metricData.benchmark / 10) * 100}%` }}
                                ></div>*/}
                                    </div>
                                    <span className="combined-personality-score">
                                      {" "}
                                      {team.avgScore}/10{" "}
                                    </span>
                                  </div>
                                  {/*<span className="combined-personality-range">Range: {metricData.team1Range}</span>*/}
                                </div>
                                {/* <div className="combined-personality-team-bar"> */}
                                {/* <span className="combined-personality-team-label"> */}
                                {/* {combinedData.teams[1].name} */}
                                {/* </span> */}
                                {/* <div className="combined-personality-progress-container">
                                    <div className="combined-personality-progress-bar"> */}
                                {/* <div
                                        className="combined-personality-progress-fill combined-personality-team2"
                                        style={{
                                          width: `${
                                            (metricData.team2Score / 10) * 100
                                          }%`,
                                        }}
                                      ></div> */}
                                {/*<div
                                  className="combined-personality-benchmark-line"
                                  style={{ left: `${(metricData.benchmark / 10) * 100}%` }}
                                ></div>*/}
                                {/* </div> */}
                                {/* <span className="combined-personality-score"> */}
                                {/* {metricData.team2Score}/10 */}
                                {/* </span> */}
                                {/* </div> */}
                                {/*<span className="combined-personality-range">Range: {metricData.team2Range}</span>*/}
                                {/* </div> */}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )
                  )}
                </div>
              </div>
            )}
          </div>
        );

      case "leadershipMetrics":
        return (
          <div className="combined-section-content">
            {renderSubTabs("leadershipMetrics")}
            {activeSubTab === "analysis" ? (
              <div className="combined-analysis-container">
                <h3>Leadership Analysis</h3>
                <div className="combined-analysis-content">
                  <p>
                    {comparisonData?.detailedReport?.leadershipMetrics?.summary}
                  </p>
                </div>
              </div>
            ) : (
              <div>
                <h3 style={{ marginBottom: "20px" }}>
                  Leadership Skills Comparison
                </h3>
                <p style={{ marginBottom: "20px" }}>
                  Percentage attainment by readiness category for each team vs.
                  benchmark, visualized with progress bars for quick comparison.
                </p>
                <div className="combined-leadership-metrics-grid">
                  {comparisonData?.detailedReport?.leadershipMetrics?.metrics.map(
                    (metricData, index) => (
                      <div
                        key={index}
                        className="combined-leadership-metric-card"
                      >
                        <div className="combined-leadership-metric-header">
                          <h4>{metricData.name}</h4>
                          <span className="combined-leadership-benchmark">
                            Benchmark: {metricData.benchmarkValue}
                          </span>
                        </div>
                        <div className="combined-personality-comparison-bars">
                          <div className="combined-personality-team-comparison">
                            <div className="combined-personality-team-bar">
                              <span className="combined-personality-team-label">
                                {metricData.teamA.name}
                              </span>
                              <div className="combined-personality-progress-container">
                                <div className="combined-personality-progress-bar">
                                  <div
                                    className="combined-leadership-progress-fill combined-leadership-team1"
                                    style={{
                                      width: `${metricData.teamA.avgReadiness}%`,
                                    }}
                                  ></div>
                                  {/* <div
                                      className="combined-leadership-benchmark-line"
                                      style={{ left: `${metricData.benchmark}%` }}
                                    ></div> */}
                                </div>
                                <span className="combined-personality-score">
                                  {metricData.teamA.avgReadiness}%
                                </span>
                              </div>
                            </div>
                            <div className="combined-personality-team-bar">
                              <span className="combined-personality-team-label">
                                {metricData.teamB.name}
                              </span>
                              <div className="combined-personality-progress-container">
                                <div className="combined-personality-progress-bar">
                                  <div
                                    className="combined-leadership-progress-fill combined-leadership-team2"
                                    style={{
                                      width: `${metricData.teamB.avgReadiness}%`,
                                    }}
                                  ></div>
                                  {/* <div
                                        className="combined-leadership-benchmark-line"
                                        style={{ left: `${metricData.benchmark}%` }}
                                      ></div> */}
                                </div>
                                <span className="combined-leadership-score">
                                  {metricData.teamB.avgReadiness}%
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )
                  )}
                </div>
              </div>
            )}
          </div>
        );

      case "summary":
        return (
          <div className="combined-section-content">
            <div className="combined-summary-content">
              <div className="combined-summary-section">
                <h4>Skill Gap Analysis</h4>
                <p>{comparisonData?.detailedReport?.softSkillGaps?.summary}</p>
              </div>
              <div className="combined-summary-section">
                <h4>Personality Distribution Analysis</h4>
                <p>
                  {comparisonData?.detailedReport?.personalityMetrics?.summary}
                </p>
              </div>
              <div className="combined-summary-section">
                <h4>Leadership Metrics Analysis</h4>
                <p>
                  {comparisonData?.detailedReport?.leadershipMetrics?.summary}
                </p>
              </div>
            </div>
          </div>
        );

      default:
        return <div>Section not found</div>;
    }
  };

  const handleDownloadPDF = () => {
    const element = document.createElement("a");
    const file = new Blob(
      [
        `Combined Talent Analysis Report\n\nGenerated on: ${new Date().toLocaleDateString()}`,
      ],
      {
        type: "text/plain",
      }
    );
    element.href = URL.createObjectURL(file);
    element.download = `combined-talent-report-${Date.now()}.txt`;
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  const generateDefaultFilename = () => {
    const date = new Date();
    const dateStr = date.toISOString().split("T")[0];
    return `combined-talent-report-${dateStr}`;
  };

  const handleSaveReport = () => {
    const defaultName = generateDefaultFilename();
    setFilename(defaultName);
    setShowFilenameModal(true);
  };

  const confirmSaveReport = () => {
    if (!filename.trim()) {
      alert("Please enter a filename");
      return;
    }
    setShowFilenameModal(false);
    setIsSaving(true);
    setTimeout(() => {
      setIsSaving(false);
      setShowSaveModal(true);
    }, 1500);
  };

  // Reset sub tab when changing sections
  const handleSectionChange = (sectionId) => {
    setActiveSection(sectionId);
    setActiveSubTab("analysis");
  };

  return (
    <div className="detailed-comparison-page">
      <BackButton />

      <main className="detailed-comparison-main">
        <div className="container">
          {/* Header */}
          <div className="detailed-comparison-header">
            <div className="detailed-header-content">
              <div className="detailed-header-text">
                <h1>{combinedData.title}</h1>
                <p>Comprehensive skill gap analysis and recommendations</p>
              </div>
              <div
                className="detailed-header-actions"
                style={{ display: "none" }}
              >
                {/* <button
                  className="detailed-action-btn detailed-secondary"
                  onClick={handleDownloadPDF}
                >
                  <Download size={16} />
                  Download PDF
                </button> */}
                <button
                  className="detailed-action-btn detailed-primary"
                  onClick={handleSaveReport}
                  disabled={isSaving}
                >
                  {isSaving ? (
                    <>
                      <RefreshCw size={16} className="spinning" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save size={16} />
                      Save Report
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>

          <div className="detailed-comparison-layout">
            {/* Navigation Sidebar */}
            <div className="detailed-navigation">
              <div className="detailed-nav-header">
                <h3>Report Sections</h3>
              </div>
              <nav className="detailed-nav-menu">
                {sections.map((section) => {
                  const IconComponent = section.icon;
                  return (
                    <button
                      key={section.id}
                      className={`detailed-nav-item ${
                        activeSection === section.id ? "active" : ""
                      }`}
                      onClick={() => setActiveSection(section.id)}
                    >
                      <IconComponent size={18} />
                      <span>{section.label}</span>
                    </button>
                  );
                })}
              </nav>
            </div>

            {/* Main Content */}
            <div className="detailed-content">
              <div className="detailed-content-header">
                <h2>{sections.find((s) => s.id === activeSection)?.label}</h2>
              </div>
              {renderSection()}
            </div>
          </div>
        </div>
      </main>

      {/* Modals */}
      {showFilenameModal && (
        <Modal
          show={showFilenameModal}
          onClose={() => setShowFilenameModal(false)}
          type="info"
          title="Save Report"
          showButton={false}
        >
          <div className="detailed-filename-modal-content">
            <div className="detailed-form-group">
              <label htmlFor="filename">Report Filename</label>
              <input
                type="text"
                id="filename"
                value={filename}
                onChange={(e) => setFilename(e.target.value)}
                placeholder="Enter filename..."
                className="detailed-filename-input"
                autoFocus
              />
            </div>
            <div className="detailed-modal-actions">
              <button
                className="detailed-modal-btn detailed-secondary"
                onClick={() => setShowFilenameModal(false)}
              >
                Cancel
              </button>
              <button
                className="detailed-modal-btn detailed-primary"
                onClick={confirmSaveReport}
                disabled={!filename.trim()}
              >
                Save Report
              </button>
            </div>
          </div>
        </Modal>
      )}

      {showSaveModal && (
        <Modal
          show={showSaveModal}
          onClose={() => setShowSaveModal(false)}
          type="success"
          title="Report Saved Successfully!"
          message={`Your skill gap report "${filename}" has been saved.`}
          showButton={true}
        />
      )}
    </div>
  );
};

export default CombinedTalentReport;
